import React, { useState, useRef, useEffect } from "react";
import "./terminal.scss";
import resumePdf from "../../assets/resume/Jacob Rajan resume.pdf";

const COMMANDS = {
  help: {
    description: "List all available commands",
    execute: () => {
      const cmds = Object.entries(COMMANDS)
        .map(([name, cmd]) => `  ${name.padEnd(14)} ${cmd.description}`)
        .join("\n");
      return `\n${cmds}\n`;
    },
  },
  whoami: {
    description: "Display user info",
    execute: () =>
      `\nJacob Rajan - Full-stack Developer\n4+ years of professional experience in web and mobile development.\nSpecializing in building exceptional digital experiences.\n`,
  },
  skills: {
    description: "List technical skills",
    execute: () =>
      `\nFrontend:   HTML, CSS, JavaScript, React, Next.js, Angular\nBackend:    Node.js, NestJS, Express\nDatabase:   MongoDB\nMobile:     React Native, Flutter\n`,
  },
  experience: {
    description: "Show work experience",
    execute: () =>
      `\nSoftware Engineer @ Stealth Startup (Jan 2026 - Present)\n  Building innovative products from the ground up.\n  Driving full-stack development and core architecture.\n\nSoftware Engineer @ Datasirpi (Nov 2023 - Dec 2025)\n  Spearheaded 6+ enterprise apps, 95% client satisfaction.\n  Reduced dev time by 30%. Mentored 4 junior developers.\n\nFreelance Developer @ Wolphix (May 2020 - Nov 2023)\n  Built school management systems, 15+ landing pages.\n  Increased client conversion rates by 35%.\n\nGraphic Designer (Feb 2017 - May 2020)\n  Designed for 25+ clients. Minimalist branding and UI/UX.\n`,
  },
  projects: {
    description: "List recent projects",
    execute: () =>
      `\nCraftpix     Digital asset marketplace\nViva Vita    Lifestyle & wellness platform\nE-Basket     E-commerce application\n`,
  },
  contact: {
    description: "Show contact details",
    execute: () =>
      `\nEmail:      imjacobrajan@gmail.com\nPhone:      +91 63815 19592\nLinkedIn:   linkedin.com/in/imjacobrajan\nGitHub:     github.com/imjacobrajan\n`,
  },
  education: {
    description: "Show education",
    execute: () =>
      `\nBachelor of Computer Applications\nSpecialization in Web Technologies\n`,
  },
  resume: {
    description: "Download resume",
    execute: () => {
      const link = document.createElement('a');
      link.href = resumePdf;
      link.download = 'Jacob Rajan Resume.pdf';
      link.click();
      return `\nDownloading Jacob Rajan Resume.pdf...\n`;
    },
  },
  clear: {
    description: "Clear terminal",
    execute: () => "__CLEAR__",
  },
};

const WELCOME_MSG = `Last login: ${new Date().toDateString()} on ttys000\nType 'help' to see available commands.`;

function Terminal({ isOpen, onClose }) {
  const [history, setHistory] = useState([{ type: "output", text: WELCOME_MSG }]);
  const [input, setInput] = useState("");
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const inputRef = useRef(null);
  const terminalBodyRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (terminalBodyRef.current) {
      terminalBodyRef.current.scrollTop = terminalBodyRef.current.scrollHeight;
    }
  }, [history]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = input.trim().toLowerCase();

    if (!trimmed) return;

    const newHistory = [
      ...history,
      { type: "input", text: trimmed },
    ];

    setCommandHistory((prev) => [trimmed, ...prev]);
    setHistoryIndex(-1);

    if (COMMANDS[trimmed]) {
      const result = COMMANDS[trimmed].execute();
      if (result === "__CLEAR__") {
        setHistory([]);
      } else {
        setHistory([...newHistory, { type: "output", text: result }]);
      }
    } else {
      setHistory([
        ...newHistory,
        {
          type: "error",
          text: `zsh: command not found: ${trimmed}`,
        },
      ]);
    }

    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (historyIndex < commandHistory.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      } else {
        setHistoryIndex(-1);
        setInput("");
      }
    }
  };

  const handleBodyClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="terminal-overlay" onClick={onClose}>
      <div className="terminal-window" onClick={(e) => e.stopPropagation()}>
        <div className="terminal-header">
          <div className="terminal-dots">
            <span className="dot red" onClick={onClose}></span>
            <span className="dot yellow"></span>
            <span className="dot green"></span>
          </div>
          <div className="terminal-title">jacob — zsh — 80x24</div>
          <div className="terminal-dots" style={{ visibility: "hidden" }}>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        </div>
        <div className="terminal-body" ref={terminalBodyRef} onClick={handleBodyClick}>
          {history.map((entry, i) => (
            <div key={i} className={`terminal-line ${entry.type}`}>
              {entry.type === "input" && (
                <span className="terminal-prompt"><span className="prompt-user">jacob</span><span className="prompt-at">@</span><span className="prompt-host">portfolio</span><span className="prompt-symbol"> ~ %  </span></span>
              )}
              <pre>{entry.text}</pre>
            </div>
          ))}
          <form onSubmit={handleSubmit} className="terminal-input-line">
            <span className="terminal-prompt"><span className="prompt-user">jacob</span><span className="prompt-at">@</span><span className="prompt-host">portfolio</span><span className="prompt-symbol"> ~ %  </span></span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="terminal-input"
              autoComplete="off"
              spellCheck="false"
            />
          </form>
        </div>
      </div>
    </div>
  );
}

export default Terminal;
